import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';

export interface ButtonConfig {
  id: string;
  icon: string;
  label: string;
  color: string;
  path: string;
  enabled: boolean;
}

/** All built-in buttons available for the home screen */
const DEFAULT_BUTTONS: ButtonConfig[] = [
  { id: 'dialer',       icon: '\u{260E}\u{FE0F}',  label: 'Dialer',       color: '#38A169', path: '/dialer',       enabled: true },
  { id: 'contacts',     icon: '\u{1F4DE}', label: 'Contacts',     color: '#4299E1', path: '/contacts',     enabled: true },
  { id: 'photos',       icon: '\u{1F4F7}', label: 'Photos',       color: '#48BB78', path: '/photos',       enabled: true },
  { id: 'info',         icon: '\u{1F4CB}', label: 'Info',         color: '#9F7AEA', path: '/info',         enabled: true },
  { id: 'games',        icon: '\u{1F3AE}', label: 'Games',        color: '#E53E3E', path: '/games',        enabled: true },
  { id: 'voicemail',    icon: '\u{1F4EC}', label: 'Voicemail',    color: '#DD6B20', path: '/voicemail',    enabled: true },
  { id: 'call-history', icon: '\u{1F4DE}', label: 'Call History',  color: '#3182CE', path: '/call-history', enabled: true },
  { id: 'settings',     icon: '\u{2699}\u{FE0F}',  label: 'Settings',     color: '#718096', path: '/settings',     enabled: true },
];

function getStorageKey(userId: number | string): string {
  return `caro_home_buttons_${userId}`;
}

/** Load saved button config for a user, merging with defaults */
export function loadButtonConfig(userId: number | string): ButtonConfig[] {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return DEFAULT_BUTTONS;

    const saved: ButtonConfig[] = JSON.parse(raw);
    // Merge: keep saved order & enabled state, but ensure all default buttons exist
    const savedMap = new Map(saved.map(b => [b.id, b]));
    const merged: ButtonConfig[] = [];

    // First: buttons in saved order
    for (const s of saved) {
      const def = DEFAULT_BUTTONS.find(d => d.id === s.id);
      if (def) {
        merged.push({ ...def, enabled: s.enabled });
      }
    }

    // Then: any new defaults not in saved config (appended at end, disabled)
    for (const d of DEFAULT_BUTTONS) {
      if (!savedMap.has(d.id)) {
        merged.push({ ...d, enabled: false });
      }
    }

    return merged;
  } catch {
    return DEFAULT_BUTTONS;
  }
}

function saveButtonConfig(userId: number | string, buttons: ButtonConfig[]) {
  try {
    localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify(buttons.map(b => ({ id: b.id, enabled: b.enabled }))),
    );
  } catch { /* localStorage not available */ }
}

export function HomeScreenSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const userId = user?.id || 'default';

  const [buttons, setButtons] = useState<ButtonConfig[]>(() => loadButtonConfig(userId));
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Persist on every change
  useEffect(() => {
    saveButtonConfig(userId, buttons);
  }, [buttons, userId]);

  const toggleButton = useCallback((id: string) => {
    setButtons(prev => prev.map(b =>
      b.id === id ? { ...b, enabled: !b.enabled } : b
    ));
  }, []);

  const moveUp = useCallback((index: number) => {
    if (index <= 0) return;
    setButtons(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  }, []);

  const moveDown = useCallback((index: number) => {
    setButtons(prev => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  }, []);

  const resetToDefault = useCallback(() => {
    setButtons(DEFAULT_BUTTONS);
  }, []);

  const enabledCount = buttons.filter(b => b.enabled).length;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      padding: '24px',
      backgroundColor: 'var(--caro-bg)',
    }}>
      {/* Header */}
      <header style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
        <button
          onClick={() => navigate('/settings')}
          style={{
            width: '48px',
            height: '48px',
            backgroundColor: 'var(--caro-card-bg)',
            border: '1px solid var(--caro-border)',
            borderRadius: '12px',
            fontSize: 'var(--font-body)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label={t.common.goBack}
        >
          &#8592;
        </button>
        <div>
          <h1 style={{ fontSize: 'var(--font-heading)', fontWeight: 600, color: 'var(--caro-text)', margin: 0 }}>
            {t.homeScreenSettings.title}
          </h1>
          <p style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)', margin: '4px 0 0' }}>
            {t.homeScreenSettings.subtitle}
          </p>
        </div>
      </header>
      {/* Enabled count */}
      <div style={{
        fontSize: 'var(--font-body)',
        color: 'var(--caro-text-light)',
        marginBottom: '16px',
        padding: '0 4px',
      }}>
        {t.homeScreenSettings.buttonCount.replace('{count}', String(enabledCount)).replace('{total}', String(buttons.length))}
      </div>
      {/* Button list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
        {buttons.map((btn, index) => (
          <div
            key={btn.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              backgroundColor: 'var(--caro-card-bg)',
              borderRadius: '14px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              opacity: btn.enabled ? 1 : 0.5,
              transition: 'opacity 0.2s',
            }}
          >
            {/* Reorder arrows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                style={{
                  width: '32px',
                  height: '24px',
                  minHeight: '24px',
                  minWidth: '32px',
                  padding: 0,
                  backgroundColor: index === 0 ? '#F7FAFC' : '#EDF2F7',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: 'var(--font-body)',
                  color: index === 0 ? '#CBD5E0' : '#4A5568',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: index === 0 ? 'default' : 'pointer',
                }}
                aria-label={`Move ${btn.label} up`}
              >
                &#9650;
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === buttons.length - 1}
                style={{
                  width: '32px',
                  height: '24px',
                  minHeight: '24px',
                  minWidth: '32px',
                  padding: 0,
                  backgroundColor: index === buttons.length - 1 ? '#F7FAFC' : '#EDF2F7',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: 'var(--font-body)',
                  color: index === buttons.length - 1 ? '#CBD5E0' : '#4A5568',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: index === buttons.length - 1 ? 'default' : 'pointer',
                }}
                aria-label={`Move ${btn.label} down`}
              >
                &#9660;
              </button>
            </div>

            {/* Icon */}
            <span style={{
              fontSize: 'var(--font-heading)',
              lineHeight: 1,
              width: '40px',
              textAlign: 'center',
            }}>
              {btn.icon}
            </span>

            {/* Label */}
            <div style={{ flex: 1 }}>
              <span style={{
                fontSize: 'var(--font-body)',
                fontWeight: 600,
                color: 'var(--caro-text)',
              }}>
                {btn.label}
              </span>
              <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)', marginTop: '2px' }}>
                {btn.path}
              </div>
            </div>

            {/* Toggle */}
            <button
              onClick={() => toggleButton(btn.id)}
              style={{
                width: '52px',
                height: '30px',
                minHeight: '30px',
                minWidth: '52px',
                padding: 0,
                borderRadius: '15px',
                border: 'none',
                backgroundColor: btn.enabled ? '#48BB78' : '#CBD5E0',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              aria-label={`${btn.enabled ? 'Hide' : 'Show'} ${btn.label}`}
              role="switch"
              aria-checked={btn.enabled}
            >
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                position: 'absolute',
                top: '3px',
                left: btn.enabled ? '25px' : '3px',
                transition: 'left 0.2s',
              }} />
            </button>
          </div>
        ))}
      </div>
      {/* Reset button */}
      <button
        onClick={resetToDefault}
        style={{
          width: '100%',
          padding: '14px',
          backgroundColor: 'var(--caro-card-bg)',
          color: 'var(--caro-text-light)',
          fontSize: 'var(--font-body)',
          fontWeight: 500,
          border: '1px solid var(--caro-border)',
          borderRadius: '12px',
        }}
      >
        {t.homeScreenSettings.resetToDefault}
      </button>
    </div>
  );
}
